import {
  Briefcase,
  Globe,
  Zap,
  Users,
  Heart,
  Book,
  Music,
  Coffee,
  Leaf,
  UtensilsCrossed,
  DollarSign,
  Activity,
  Landmark,
  Palette,
  Scroll,
  Trophy,
  Sparkles,
  LayoutGrid,
  LucideIcon,
} from "lucide-react";

export interface CategoryMeta {
  icon: LucideIcon;
  gradient: string;
  iconColor: string;
  badgeColor: string;
  activeClass: string;
  hoverClass: string;
  bannerUrl: string;
}

// Map category to visual metadata
export const getCategoryMeta = (category: string): CategoryMeta => {
  switch (category) {
    case "Business":
      return {
        icon: Briefcase,
        gradient: "from-blue-600 via-blue-500 to-sky-500",
        iconColor: "text-blue-500",
        badgeColor: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20",
        activeClass: "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/25",
        hoverClass: "hover:bg-blue-50 text-blue-700 hover:text-blue-900 border-blue-200",
        bannerUrl: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80",
      };
    case "Travel":
      return {
        icon: Globe,
        gradient: "from-sky-600 via-blue-500 to-indigo-500",
        iconColor: "text-sky-500",
        badgeColor: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20",
        activeClass: "bg-sky-600 text-white hover:bg-sky-700 shadow-sky-500/25",
        hoverClass: "hover:bg-sky-50 text-sky-700 hover:text-sky-900 border-sky-200",
        bannerUrl: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80",
      };
    case "Technology":
      return {
        icon: Zap,
        gradient: "from-sky-600 via-cyan-500 to-teal-500",
        iconColor: "text-sky-500",
        badgeColor: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20",
        activeClass: "bg-sky-600 text-white hover:bg-sky-700 shadow-sky-500/25",
        hoverClass: "hover:bg-sky-50 text-sky-700 hover:text-sky-900 border-sky-200",
        bannerUrl: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
      };
    case "Social":
      return {
        icon: Users,
        gradient: "from-orange-600 via-amber-500 to-yellow-500",
        iconColor: "text-orange-500",
        badgeColor: "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20",
        activeClass: "bg-orange-600 text-white hover:bg-orange-700 shadow-orange-500/25",
        hoverClass: "hover:bg-orange-50 text-orange-700 hover:text-orange-900 border-orange-200",
        bannerUrl: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80",
      };
    case "Lifestyle":
      return {
        icon: Heart,
        gradient: "from-rose-600 via-orange-500 to-amber-500",
        iconColor: "text-rose-500",
        badgeColor: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20",
        activeClass: "bg-rose-600 text-white hover:bg-rose-700 shadow-rose-500/25",
        hoverClass: "hover:bg-rose-50 text-rose-700 hover:text-rose-900 border-rose-200",
        bannerUrl: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&q=80",
      };
    case "Education":
      return {
        icon: Book,
        gradient: "from-cyan-600 via-blue-500 to-indigo-500",
        iconColor: "text-cyan-500",
        badgeColor: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/20",
        activeClass: "bg-cyan-600 text-white hover:bg-cyan-700 shadow-cyan-500/25",
        hoverClass: "hover:bg-cyan-50 text-cyan-700 hover:text-cyan-900 border-cyan-200",
        bannerUrl: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=80",
      };
    case "Entertainment":
      return {
        icon: Music,
        gradient: "from-yellow-500 via-orange-500 to-red-500",
        iconColor: "text-yellow-600",
        badgeColor: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-500/20",
        activeClass: "bg-yellow-600 text-white hover:bg-yellow-700 shadow-yellow-500/25",
        hoverClass: "hover:bg-yellow-50 text-yellow-700 hover:text-yellow-900 border-yellow-200",
        bannerUrl: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&q=80",
      };
    case "Daily Life":
      return {
        icon: Coffee,
        gradient: "from-amber-600 via-orange-500 to-rose-500",
        iconColor: "text-amber-600",
        badgeColor: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
        activeClass: "bg-amber-600 text-white hover:bg-amber-700 shadow-amber-500/25",
        hoverClass: "hover:bg-amber-50 text-amber-700 hover:text-amber-900 border-amber-200",
        bannerUrl: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&q=80",
      };
    case "Environment":
      return {
        icon: Leaf,
        gradient: "from-emerald-600 via-teal-500 to-cyan-500",
        iconColor: "text-blue-600",
        badgeColor: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20",
        activeClass: "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/25",
        hoverClass: "hover:bg-blue-50 text-blue-700 hover:text-blue-900 border-blue-200",
        bannerUrl: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80",
      };
    case "Food & Cooking":
      return {
        icon: UtensilsCrossed,
        gradient: "from-orange-600 via-red-500 to-rose-500",
        iconColor: "text-orange-600",
        badgeColor: "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20",
        activeClass: "bg-orange-600 text-white hover:bg-orange-700 shadow-orange-500/25",
        hoverClass: "hover:bg-orange-50 text-orange-700 hover:text-orange-900 border-orange-200",
        bannerUrl: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80",
      };
    case "Finance":
      return {
        icon: DollarSign,
        gradient: "from-amber-600 via-yellow-500 to-orange-500",
        iconColor: "text-amber-600",
        badgeColor: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
        activeClass: "bg-amber-600 text-white hover:bg-amber-700 shadow-amber-500/25",
        hoverClass: "hover:bg-amber-50 text-amber-700 hover:text-amber-900 border-amber-200",
        bannerUrl: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80",
      };
    case "Health & Fitness":
      return {
        icon: Activity,
        gradient: "from-red-600 via-rose-500 to-orange-500",
        iconColor: "text-red-600",
        badgeColor: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20",
        activeClass: "bg-red-600 text-white hover:bg-red-700 shadow-red-500/25",
        hoverClass: "hover:bg-red-50 text-red-700 hover:text-red-900 border-red-200",
        bannerUrl: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&q=80",
      };
    case "Politics":
      // Politics is the one category that keeps a cool neutral. Every other
      // neutral surface moved to warm stone, which had left Politics and
      // History rendering identically; slate is what tells them apart.
      return {
        icon: Landmark,
        gradient: "from-stone-600 via-stone-500 to-stone-700",
        iconColor: "text-stone-600",
        badgeColor: "bg-stone-500/10 text-stone-700 dark:text-stone-300 border-stone-500/20",
        activeClass: "bg-stone-600 text-white hover:bg-stone-700 shadow-stone-500/25",
        hoverClass: "hover:bg-stone-50 text-stone-700 hover:text-stone-900 border-stone-200",
        bannerUrl: "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=800&q=80",
      };
    case "Art & Design":
      return {
        icon: Palette,
        gradient: "from-teal-600 via-cyan-500 to-sky-500",
        iconColor: "text-teal-600",
        badgeColor: "bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-500/20",
        activeClass: "bg-teal-600 text-white hover:bg-teal-700 shadow-teal-500/25",
        hoverClass: "hover:bg-teal-50 text-teal-700 hover:text-teal-900 border-teal-200",
        bannerUrl: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=800&q=80",
      };
    case "History":
      return {
        icon: Scroll,
        gradient: "from-stone-600 via-amber-500 to-yellow-500",
        iconColor: "text-stone-600",
        badgeColor: "bg-stone-500/10 text-stone-700 dark:text-stone-300 border-stone-500/20",
        activeClass: "bg-stone-600 text-white hover:bg-stone-700 shadow-stone-500/25",
        hoverClass: "hover:bg-stone-50 text-stone-700 hover:text-stone-900 border-stone-200",
        bannerUrl: "https://images.unsplash.com/photo-1461360370896-922624d12aa1?w=800&q=80",
      };
    case "Sports":
      return {
        icon: Trophy,
        gradient: "from-sky-600 via-blue-500 to-indigo-500",
        iconColor: "text-sky-600",
        badgeColor: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20",
        activeClass: "bg-sky-600 text-white hover:bg-sky-700 shadow-sky-500/25",
        hoverClass: "hover:bg-sky-50 text-sky-700 hover:text-sky-900 border-sky-200",
        bannerUrl: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&q=80",
      };
    default:
      return {
        // Not Sparkles: that glyph means "AI-generated" everywhere else, and a
        // category is a bucket, not a claim. It was also doing four jobs on
        // Browse alone (this chip, the AI-Powered badge, the sidebar's "All",
        // and the Create Topic action).
        icon: LayoutGrid,
        gradient: "from-blue-600 via-sky-500 to-cyan-500",
        iconColor: "text-primary",
        badgeColor: "bg-primary/10 text-blue-700 dark:text-blue-300 border-primary/20",
        activeClass: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary/25",
        hoverClass: "hover:bg-primary/10 text-primary hover:text-primary-foreground border-primary/20",
        bannerUrl: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80",
      };
  }
};

